/**
 * Subscription factory for setting up all Supabase real-time subscriptions
 * Uses declarative config from subscriptionConfig.ts
 */

import { type RealtimeChannel } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react-native';
import { SupabaseFetchError } from '../../utils/supabaseFetchError';
import {
  createRealtimeChannel,
  createOrganizationRealtimeChannel,
  type RealtimeStateListener,
} from './supabaseClient';
import {
  type SubscriptionConfig,
  SUBSCRIPTION_CONFIGS,
  getTableName,
} from './subscriptionConfig';
import {
  fetchProductsFromSupabase,
  fetchProductCategoriesFromSupabase,
  fetchStoresFromSupabase,
  fetchUsersFromSupabase,
  fetchRolesFromSupabase,
  fetchPaymentMethodsFromSupabase,
  fetchPurchaseMethodsFromSupabase,
  fetchProductUnitsFromSupabase,
  fetchWalletsFromSupabase,
  fetchDiscountOptionsFromSupabase,
  fetchSpendingCategoriesFromSupabase,
  fetchWriteOffReasonsFromSupabase,
  fetchReturnReasonsFromSupabase,
  fetchPriceTypesFromSupabase,
  fetchProductModifiersFromSupabase,
  fetchSaleSettingsFromSupabase,
  fetchLoyaltySettingsFromSupabase,
  fetchRecurringSpendingsFromSupabase,
  fetchComplexSaleTemplatesFromSupabase,
  fetchSuppliersFromSupabase,
  fetchOrganizationFromSupabase,
  fetchPrintTemplatesFromSupabase,
} from '../../utils/supabaseDataHelpers';
import { productsApi } from '../API/typedApiClient';
import {
  abandonLocalViewRead,
  beginLocalViewRefetch,
  commitLocalViewRead,
  registerLocalViewReadRequester,
} from '../Write/localView';
import { getLocalStoreDate } from '../../utils/timezoneFunctions';

/**
 * Map of entity names to their fetch functions
 */
const FETCH_FUNCTIONS: Record<string, (orgId: string) => Promise<unknown>> = {
  roles: fetchRolesFromSupabase,
  productCategories: fetchProductCategoriesFromSupabase,
  suppliers: fetchSuppliersFromSupabase,
  productUnits: fetchProductUnitsFromSupabase,
  productModifiers: fetchProductModifiersFromSupabase,
  products: fetchProductsFromSupabase,
  stores: fetchStoresFromSupabase,
  users: fetchUsersFromSupabase,
  organization: fetchOrganizationFromSupabase,
  paymentMethods: fetchPaymentMethodsFromSupabase,
  purchaseMethods: fetchPurchaseMethodsFromSupabase,
  wallets: fetchWalletsFromSupabase,
  discountOptions: fetchDiscountOptionsFromSupabase,
  spendingCategories: fetchSpendingCategoriesFromSupabase,
  writeOffReasons: fetchWriteOffReasonsFromSupabase,
  returnReasons: fetchReturnReasonsFromSupabase,
  priceTypes: fetchPriceTypesFromSupabase,
  saleSettings: fetchSaleSettingsFromSupabase,
  loyaltySettings: fetchLoyaltySettingsFromSupabase,
  recurringSpendings: fetchRecurringSpendingsFromSupabase,
  complexSaleTemplates: fetchComplexSaleTemplatesFromSupabase,
  // `printTemplates` is the one entry whose fetcher returns an ARRAY rather than a dict, because
  // `printTemplatesAtom` is `PrintTemplate[]`. `updateState`'s `atom` target passes the fetched
  // value straight to the setter, and `removeFromDict` below is array-aware, so both directions
  // preserve that shape. It previously had no fetcher at all and was `clearOnly` — see
  // `subscriptionConfig.ts`.
  printTemplates: fetchPrintTemplatesFromSupabase,
};

/**
 * Context object passed to setupAllSubscriptions
 * Contains all the setters and callbacks needed by subscriptions
 */
export interface SubscriptionContext {
  // Organization IDs
  supabaseOrgId: string;
  firestoreOrgId: string;

  // Atom setters
  setConstants: (fn: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  setRoles: (data: Record<string, unknown>) => void;
  setProductCategories: (data: Record<string, unknown>) => void;
  setSuppliersDict: (data: Record<string, unknown>) => void;
  setProductsDict: (data: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void;
  setProductsUpdated: (fn: (prev: { updateCount: number }) => { updateCount: number }) => void;
  setProductUnitsDict: (data: Record<string, unknown>) => void;
  setDiscountOptionsList: (data: unknown[]) => void;
  setStores: (data: Record<string, unknown>) => void;
  setUsersDict: (data: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void;
  setInactiveUsers: (data: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void;
  setOrganization: (data: unknown) => void;
  setPrintTemplates: (data: unknown[]) => void;

  // Callbacks
  resubscribeDailyStats: (stores: Record<string, unknown>) => void;
  setUserInfoToAnalytics: (user: unknown, org: unknown) => void;

  // i18n
  t: (key: string) => string;

  // User context (for analytics)
  user: { organizationId: string; [key: string]: unknown };

  /**
   * The store the product catalogue was loaded for. `GET /products` computes per-store stock from
   * it, so a full products resync must pass the same store the initial load did.
   */
  storeId?: string;

  // Debounce tracking (will be populated by factory)
  pendingProductRefetches?: Map<string, ReturnType<typeof setTimeout>>;

  /**
   * Set by `cleanupPendingRefetches` when the subscriptions built from this context are torn down.
   * A refetch still in flight at that moment belongs to a session that has been replaced, and
   * writing its result would overwrite the newer session's state with an older read.
   */
  disposed?: boolean;

  /** ATL-1445 stage 3 P6: removes this context's local-view read requester (set by `setupAllSubscriptions`). */
  unregisterLocalViewReads?: () => void;
}

/**
 * Helper to schedule a debounced product refetch
 * Used by both products subscription and productStock subscription
 */
async function scheduleDebouncedProductRefetch(
  productId: string,
  debounceMs: number,
  context: SubscriptionContext,
  source: 'products' | 'productStock' | 'localViewAck'
): Promise<void> {
  const pendingRefetches = context.pendingProductRefetches;

  // Cancel any pending refetch for this product
  if (pendingRefetches?.has(productId)) {
    clearTimeout(pendingRefetches.get(productId));
  }

  // Schedule debounced refetch
  const timeoutId = setTimeout(async () => {
    pendingRefetches?.delete(productId);
    // ATL-1445 stage 3 P6: the local view's products generation. A read that STARTS here, after an
    // outbox entry was acknowledged, may retire that entry's stock overlay for this product once its
    // result is written (`localView.ts`). A counter; nothing else happens for a session without one.
    const generation = beginLocalViewRefetch('products');
    try {
      const fullProduct = await productsApi.getProductById(productId);
      if (fullProduct && !context.disposed) {
        const dictKey = fullProduct.firestoreId || fullProduct.id;
        // One update with the retirement it confirms: no render sees the new stock AND the overlay.
        commitLocalViewRead(generation, () => context.setProductsDict((prev) => ({ ...prev, [dictKey]: fullProduct })), dictKey);
        context.setProductsUpdated((prev) => ({ updateCount: prev.updateCount + 1 }));
      }
    } catch (error) {
      console.error(`[subscriptionFactory] Failed to refetch product (${source}) ${productId}:`, error);
      Sentry.captureException(error, {
        tags: { subscription: source, productId },
        extra: { source, productId },
      });
    } finally {
      // A read that wrote nothing settles here (a no-op after the commit above).
      abandonLocalViewRead(generation);
    }
  }, debounceMs);

  pendingRefetches?.set(productId, timeoutId);
}

/**
 * Apply transforms to fetched data based on config
 */
function applyTransforms(
  data: Record<string, unknown>,
  config: SubscriptionConfig,
  context: SubscriptionContext
): unknown {
  let result: unknown = data;

  // Add localDate (client-side calculation based on store timezone)
  if (config.addLocalDate) {
    Object.values(data).forEach((store: unknown) => {
      const storeObj = store as Record<string, unknown>;
      storeObj.localDate = getLocalStoreDate(
        (storeObj.firestoreId as string) || (storeObj.id as string)
      );
    });
  }

  // Convert dict to array
  if (config.toArray) {
    result = Object.values(data);
  }

  // Named transform functions
  if (config.transformKey === 'discountOptions') {
    const options = Object.values(data);
    const newOptions: unknown[] = [
      { title: context.t('newSale.withoutDiscount'), text: context.t('newSale.withoutDiscount'), percentage: 0 },
      { title: context.t('newSale.discountAmount'), text: context.t('newSale.discountAmount'), amount: 0 },
    ];
    options.forEach((option: unknown) => {
      const optObj = option as Record<string, unknown>;
      newOptions.push({
        title: `${optObj.text}%`,
        text: `${optObj.text}%`,
        percentage: parseFloat(optObj.text as string),
      });
    });
    result = newOptions;
  }

  return result;
}

/**
 * Update state based on target type
 */
function updateState(
  entityName: string,
  config: SubscriptionConfig,
  data: unknown,
  context: SubscriptionContext
): void {
  const { target } = config;

  switch (target.type) {
    case 'atom': {
      const setter = context[target.setter as keyof SubscriptionContext] as (data: unknown) => void;
      if (setter) {
        setter(data);
      } else {
        console.warn(`[subscriptionFactory] Setter not found: ${target.setter}`);
      }
      break;
    }

    case 'constants': {
      context.setConstants((prev) => {
        if (target.asDict) {
          // Keep as dict (saleSettings)
          return {
            ...prev,
            [target.key]: {
              ...(prev[target.key] as Record<string, unknown> || {}),
              data: data,
            },
          };
        } else {
          // Convert to array for constants
          return {
            ...prev,
            [target.key]: {
              ...(prev[target.key] as Record<string, unknown> || {}),
              data: Array.isArray(data) ? data : Object.values(data as Record<string, unknown>),
            },
          };
        }
      });
      break;
    }

    case 'constantsSingle': {
      // Single-document constants (e.g., loyaltySettings) - spread fields directly
      context.setConstants((prev) => {
        const prevEntry = prev[target.key] as Record<string, unknown> || {};
        return {
          ...prev,
          [target.key]: {
            id: target.key,
            organizationId: context.supabaseOrgId,
            ref: prevEntry.ref,
            ...(data as Record<string, unknown> || {}),
          },
        };
      });
      break;
    }

    case 'split': {
      // Split data by field value
      const dataDict = data as Record<string, unknown>;
      const groups: Record<string, Record<string, unknown>> = {};

      // Initialize groups
      Object.keys(target.targets).forEach((key) => {
        groups[key] = {};
      });

      // Split items into groups
      Object.entries(dataDict).forEach(([key, item]) => {
        const itemObj = item as Record<string, unknown>;
        const splitValue = String(itemObj[target.splitBy] ?? 'true'); // Default to 'true' for active field
        if (groups[splitValue]) {
          groups[splitValue][key] = item;
        }
      });

      // Update each target
      Object.entries(target.targets).forEach(([value, setterName]) => {
        const setter = context[setterName as keyof SubscriptionContext] as (data: unknown) => void;
        if (setter) {
          setter(groups[value] || {});
        }
      });
      break;
    }

    case 'constantsSplit': {
      // Split into multiple constants keys
      const items = Object.values(data as Record<string, unknown>);
      const groups: Record<string, unknown[]> = {};

      // Initialize groups
      Object.values(target.keys).forEach((key) => {
        groups[key] = [];
      });

      // Split items into groups
      items.forEach((item: unknown) => {
        const itemObj = item as Record<string, unknown>;
        const splitValue = String(itemObj[target.splitBy] ?? 'false');
        const targetKey = target.keys[splitValue];
        if (targetKey && groups[targetKey]) {
          groups[targetKey].push(item);
        }
      });

      // Update constants
      context.setConstants((prev) => {
        const updated = { ...prev };
        Object.entries(groups).forEach(([key, items]) => {
          updated[key] = {
            ...(prev[key] as Record<string, unknown> || {}),
            data: items,
          };
        });
        return updated;
      });
      break;
    }

    case 'clearOnly': {
      // Just clear the data (for lazy-loaded entities like printTemplates)
      const setter = context[target.setter as keyof SubscriptionContext] as (data: unknown[]) => void;
      if (setter) {
        setter([]);
      }
      break;
    }
  }
}

/**
 * Handle delete event - remove item from state
 */
/**
 * Remove an item from a dict by deleteKey.
 * Tries direct key deletion first, then scans by supabaseId as fallback
 * (deleteKey may be a UUID while dict is keyed by firestoreId).
 *
 * ARRAYS ARE HANDLED SEPARATELY. `printTemplatesAtom` holds an array, and `{ ...array }` produces
 * an OBJECT keyed by numeric strings — which is not merely the wrong shape, it silently breaks
 * every consumer (`getPrintTemplate` calls `.filter`) and fails the atom's `z.array` schema on the
 * next persist. Spreading an array into a dict is the kind of change that looks like a no-op and
 * empties a screen, so the array case is explicit rather than incidental.
 */
function removeFromDict<T extends Record<string, unknown> | unknown[]>(
  dict: T,
  deleteKey: string
): T {
  if (Array.isArray(dict)) {
    return dict.filter((item) =>
      itemDoesNotMatchKey(item as Record<string, unknown>, deleteKey)
    ) as unknown as T;
  }
  const updated: Record<string, unknown> = { ...(dict as Record<string, unknown>) };
  delete updated[deleteKey];
  for (const [dictKey, value] of Object.entries(updated)) {
    if (value && typeof value === 'object' && (value as Record<string, unknown>).supabaseId === deleteKey) {
      delete updated[dictKey];
      break;
    }
  }
  return updated as unknown as T;
}

/**
 * Check if an item does NOT match the deleteKey across all ID fields.
 */
function itemDoesNotMatchKey(item: Record<string, unknown>, deleteKey: string): boolean {
  return item.firestoreId !== deleteKey
    && item.id !== deleteKey
    && item.supabaseId !== deleteKey;
}

function handleDelete(
  entityName: string,
  config: SubscriptionConfig,
  deleteKey: string,
  context: SubscriptionContext
): void {
  const { target } = config;

  switch (target.type) {
    case 'atom': {
      // The updater is shape-preserving: `removeFromDict` is generic, so it returns an array for
      // an array atom (printTemplates) and a dict for every other one.
      const setter = context[target.setter as keyof SubscriptionContext] as (
        fn: (prev: Record<string, unknown>) => Record<string, unknown>
      ) => void;
      if (setter) {
        setter((prev) => removeFromDict(prev, deleteKey));
      }
      break;
    }

    case 'constants': {
      context.setConstants((prev) => {
        const constantData = prev[target.key] as Record<string, unknown> | undefined;
        if (!constantData?.data) return prev;

        if (target.asDict) {
          return {
            ...prev,
            [target.key]: { ...constantData, data: removeFromDict(constantData.data as Record<string, unknown>, deleteKey) },
          };
        } else {
          const dataArray = constantData.data as unknown[];
          return {
            ...prev,
            [target.key]: {
              ...constantData,
              data: dataArray.filter((item) => itemDoesNotMatchKey(item as Record<string, unknown>, deleteKey)),
            },
          };
        }
      });
      break;
    }

    case 'split': {
      Object.values(target.targets).forEach((setterName) => {
        const setter = context[setterName as keyof SubscriptionContext] as (
          fn: (prev: Record<string, unknown>) => Record<string, unknown>
        ) => void;
        if (setter) {
          setter((prev) => removeFromDict(prev, deleteKey));
        }
      });
      break;
    }

    case 'constantsSplit': {
      context.setConstants((prev) => {
        const updated = { ...prev };
        Object.values(target.keys).forEach((key) => {
          const constantData = updated[key] as Record<string, unknown> | undefined;
          if (constantData?.data && Array.isArray(constantData.data)) {
            updated[key] = {
              ...constantData,
              data: (constantData.data as unknown[]).filter((item) => itemDoesNotMatchKey(item as Record<string, unknown>, deleteKey)),
            };
          }
        });
        return updated;
      });
      break;
    }

    case 'clearOnly': {
      // Clear triggers refetch, which will handle delete
      const setter = context[target.setter as keyof SubscriptionContext] as (data: unknown[]) => void;
      if (setter) {
        setter([]);
      }
      break;
    }
  }
}

/**
 * A refetch failed. ATL-970: the caller keeps the last good copy — state is not written, so the
 * atom is left exactly as it was. Writing `{}` here is what emptied a populated list mid-shift.
 *
 * A `SupabaseFetchError` has already been reported by the fetcher, under its own `collection` tag;
 * reporting it again here would only double the volume. Anything else — a transform, a setter, a
 * callback — was previously reported by nobody at all.
 */
function reportRefetchFailure(entityName: string, error: unknown, context: SubscriptionContext): void {
  console.error(`[subscriptionFactory] Failed to refetch ${entityName}:`, error);
  if (!(error instanceof SupabaseFetchError)) {
    Sentry.captureException(error, {
      tags: { supabase_refetch_error: true, entity: entityName },
      extra: { entityName, organizationId: context.firestoreOrgId },
    });
  }
}

/**
 * Refetch one entity in full and write it to state.
 */
async function refetchEntity(
  entityName: string,
  config: SubscriptionConfig,
  context: SubscriptionContext
): Promise<void> {
  const fetchFn = FETCH_FUNCTIONS[entityName];
  if (!fetchFn) {
    console.warn(`[subscriptionFactory] No fetch function for ${entityName}`);
    return;
  }

  try {
    // Use firestoreOrgId for organization subscription, supabaseOrgId for others
    const orgId = config.filterField === 'firestore_id'
      ? context.firestoreOrgId
      : context.supabaseOrgId;

    let data = await fetchFn(orgId) as Record<string, unknown>;
    if (context.disposed) return;

    // Apply transforms
    data = applyTransforms(data, config, context) as Record<string, unknown>;

    // Update state
    updateState(entityName, config, data, context);

    // Run callback if specified
    if (config.onUpdate) {
      const callback = context[config.onUpdate as keyof SubscriptionContext] as (data: unknown) => void;
      if (callback) {
        callback(data);
      }
    }

    // Special handling for organization - also update analytics
    if (entityName === 'organization' && data) {
      context.setUserInfoToAnalytics(context.user, data);
    }
  } catch (error) {
    reportRefetchFailure(entityName, error, context);
  }
}

/**
 * Refetch the whole product catalogue — the resync for `products` AND `productStock`.
 *
 * Their event handlers refetch ONE product by the id in the payload. A resync has no payload: it
 * exists because an unknown set of products (and their stock) changed while nothing was delivered,
 * so only the full list is correct. Same request as the initial load, store included.
 */
async function refetchAllProducts(context: SubscriptionContext): Promise<void> {
  // ATL-1445 stage 3 P6: a full catalogue read covers every product (see the single-product refetch).
  const generation = beginLocalViewRefetch('products');
  try {
    const products = await fetchProductsFromSupabase(context.firestoreOrgId, context.storeId);
    if (context.disposed) return;
    commitLocalViewRead(generation, () => context.setProductsDict(products as Record<string, unknown>));
    context.setProductsUpdated((prev) => ({ updateCount: prev.updateCount + 1 }));
  } catch (error) {
    reportRefetchFailure('products', error, context);
  } finally {
    abandonLocalViewRead(generation);
  }
}

/** How long an acknowledgement's product re-read waits, so one sale's lines coalesce per product. */
const LOCAL_VIEW_ACK_REFETCH_DEBOUNCE_MS = 300;

/**
 * Single-flight per key: a request that arrives while the same key is refetching is not dropped —
 * that refetch may have been issued BEFORE the change it is now being asked about — and is not
 * doubled either. It runs once more when the current one finishes.
 */
interface ResyncScheduler {
  schedule: (key: string) => void;
  isRunning: (key: string) => boolean;
}

function createResyncScheduler(run: (key: string) => Promise<void>): ResyncScheduler {
  const inFlight = new Set<string>();
  const again = new Set<string>();
  const schedule = (key: string): void => {
    if (inFlight.has(key)) {
      again.add(key);
      return;
    }
    inFlight.add(key);
    void (async () => {
      try {
        do {
          again.delete(key);
          await run(key);
        } while (again.has(key));
      } finally {
        inFlight.delete(key);
      }
    })();
  };
  return { schedule, isRunning: (key) => inFlight.has(key) };
}

/**
 * Create subscription handler for a specific entity
 */
function createHandler(
  entityName: string,
  config: SubscriptionConfig,
  context: SubscriptionContext
): (eventType: string, ref: { id?: string; firestore_id?: string; store_id?: string; product_id?: string }) => Promise<void> {
  return async (eventType, ref) => {
    console.log(`[subscriptionFactory] ${entityName} event: ${eventType}`, ref);

    // Determine delete key based on config
    const deleteKey = config.deleteKey === 'storeId'
      ? ref.store_id || ref.id
      : ref.firestore_id || ref.id;

    // Special handling for product_store_stock DELETE - cancel pending refetches and do final refresh
    if (eventType === 'DELETE' && config.triggerProductRefetch && ref.product_id) {
      const pendingRefetches = context.pendingProductRefetches;
      if (pendingRefetches?.has(ref.product_id)) {
        clearTimeout(pendingRefetches.get(ref.product_id));
        pendingRefetches.delete(ref.product_id);
      }
      // Immediate refetch (no debounce) to update stock display after deletion
      void scheduleDebouncedProductRefetch(ref.product_id, 0, context, 'productStock');
      return;
    }

    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      // Special handling for product_store_stock - triggers product refetch
      if (config.triggerProductRefetch && ref.product_id) {
        void scheduleDebouncedProductRefetch(ref.product_id, config.debounce || 500, context, 'productStock');
        return;
      }

      // Special handling for products with debounced single-item refetch
      if (config.debounce && config.fetchSingle && entityName === 'products') {
        const productId = ref.id;
        if (!productId) return;
        void scheduleDebouncedProductRefetch(productId, config.debounce, context, 'products');
        return;
      }

      // Special handling for clearOnly. No entity uses it any more (printTemplates was the only
      // one and now refetches like everything else), but the branch stays because the target type
      // is still part of the config surface.
      if (config.target.type === 'clearOnly') {
        const setter = context[config.target.setter as keyof SubscriptionContext] as (data: unknown[]) => void;
        if (setter) {
          setter([]);
        }
        return;
      }

      // Standard handling: refetch all data
      await refetchEntity(entityName, config, context);
    } else if (eventType === 'DELETE' && deleteKey) {
      // Special handling for products - cancel pending refetch and remove
      if (config.debounce && config.fetchSingle && entityName === 'products') {
        const pendingRefetches = context.pendingProductRefetches;
        if (pendingRefetches?.has(deleteKey)) {
          clearTimeout(pendingRefetches.get(deleteKey));
          pendingRefetches.delete(deleteKey);
        }
        context.setProductsDict((prev) => removeFromDict(prev, deleteKey));
        context.setProductsUpdated((prev) => ({ updateCount: prev.updateCount + 1 }));
        return;
      }

      handleDelete(entityName, config, deleteKey, context);
    }
  };
}

const PRODUCTS_RESYNC_KEY = 'products';

/**
 * Set up all subscriptions based on config
 * @param context - Contains organization IDs, setters, and callbacks
 * @returns Array of Supabase channels for cleanup
 */
export function setupAllSubscriptions(context: SubscriptionContext): RealtimeChannel[] {
  const channels: RealtimeChannel[] = [];

  // Initialize pending product refetches map
  context.pendingProductRefetches = new Map();

  // ATL-1405: `postgres_changes` is an event stream. It delivers nothing while a channel is down
  // and replays nothing when it rejoins, so every rejoin after a gap — a transport loss, a join
  // timeout, a rejected channel that later recovers — must re-read the entity it covers, or the
  // list it feeds stays wrong until the next unrelated change or an app restart. This is the
  // ATL-1165 daily-stats fix, applied to the other 23 channels.
  const resync = createResyncScheduler((key) =>
    key === PRODUCTS_RESYNC_KEY
      ? refetchAllProducts(context)
      : refetchEntity(key, SUBSCRIPTION_CONFIGS[key], context)
  );

  // Set up each subscription
  for (const [entityName, config] of Object.entries(SUBSCRIPTION_CONFIGS)) {
    const tableName = getTableName(entityName, config);
    const channelName = `${tableName}:${context.supabaseOrgId}`;
    // `products` and `productStock` both feed the product catalogue, so they share one resync.
    const resyncKey = config.fetchSingle ? PRODUCTS_RESYNC_KEY : entityName;
    const entityHandler = createHandler(entityName, config, context);
    // A product event that arrives while the full catalogue resync is in flight may describe a
    // change that resync's read predates. Its single-product refetch (or delete) can land FIRST and
    // then be overwritten by the older full list — resurrecting a deleted product or restoring old
    // stock. Queue one more full resync behind the current one so the catalogue converges.
    const handler: typeof entityHandler = config.fetchSingle
      ? (eventType, ref) => {
          if (resync.isRunning(PRODUCTS_RESYNC_KEY)) resync.schedule(PRODUCTS_RESYNC_KEY);
          return entityHandler(eventType, ref);
        }
      : entityHandler;
    const onStateChange: RealtimeStateListener = (state, gap) => {
      if (state === 'subscribed' && gap) {
        console.log(`[subscriptionFactory] ${entityName} rejoined after a gap — resyncing`);
        resync.schedule(resyncKey);
      }
    };

    let channel: RealtimeChannel | null = null;

    if (config.filterField === 'firestore_id') {
      // Use organization-specific channel (filters by firestore_id)
      channel = createOrganizationRealtimeChannel(
        channelName,
        context.firestoreOrgId,
        (payload) => {
          // For DELETE events, payload.new is {} (truthy empty object), so use payload.old
          const raw = (payload.eventType === 'DELETE' ? payload.old : payload.new) as Record<string, unknown> | undefined;
          void handler(payload.eventType, {
            id: raw?.id as string | undefined,
            firestore_id: raw?.firestore_id as string | undefined,
          });
        },
        { onStateChange }
      );
    } else {
      // Standard channel (filters by organization_id)
      channel = createRealtimeChannel(
        channelName,
        tableName,
        context.supabaseOrgId,
        (payload) => {
          // For DELETE events, payload.new is {} (truthy empty object), so use payload.old
          const raw = (payload.eventType === 'DELETE' ? payload.old : payload.new) as Record<string, unknown> | undefined;
          void handler(payload.eventType, {
            id: raw?.id as string | undefined,
            firestore_id: raw?.firestore_id as string | undefined,
            store_id: raw?.store_id as string | undefined,
            product_id: raw?.product_id as string | undefined, // For stock updates
          });
        },
        { onStateChange }
      );
    }

    if (channel) {
      channels.push(channel);
    }
  }

  // ATL-1445 stage 3 P6: an acknowledged outbox v2 entry asks for a read of the products it touched,
  // so its overlay is confirmed by a read that started after the ack (localView.ts, reviewer B1). Never
  // called while no entry is acknowledged, i.e. never for a session today.
  context.unregisterLocalViewReads = registerLocalViewReadRequester('products', (keys) => {
    if (context.disposed) return;
    if (keys === null) {
      resync.schedule(PRODUCTS_RESYNC_KEY);
      return;
    }
    // A key already waiting for its debounced re-read keeps its timer: every drainer pass re-asks for
    // what is still unconfirmed, and resetting the timer each time would postpone the read for as long
    // as a backlog drains.
    for (const key of keys) {
      if (context.pendingProductRefetches?.has(key)) continue;
      void scheduleDebouncedProductRefetch(key, LOCAL_VIEW_ACK_REFETCH_DEBOUNCE_MS, context, 'localViewAck');
    }
  });

  console.log(`[subscriptionFactory] Set up ${channels.length} subscriptions`);
  return channels;
}

/**
 * Cleanup for a context whose subscriptions are being torn down: clears pending product refetches
 * and stops any refetch still in flight from writing into state.
 */
export function cleanupPendingRefetches(context: SubscriptionContext): void {
  context.disposed = true;
  context.unregisterLocalViewReads?.();
  if (context.pendingProductRefetches) {
    context.pendingProductRefetches.forEach((timeout) => {
      clearTimeout(timeout);
    });
    context.pendingProductRefetches.clear();
  }
}

export default setupAllSubscriptions;
